import { monitoring } from 'shared/library/monitoring';

type ResourceTree = Record<string, unknown>;
type TranslationOptions = Record<string, unknown>;
type TranslateFunction = (key: string, options: TranslationOptions) => unknown;

function describeValue(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';

  return typeof value;
}

function isMissingTranslationValue(value: unknown, key: string) {
  return typeof value === 'string' && value === key;
}

function getFallbackLanguages(fallbackLng: unknown): string[] {
  if (!fallbackLng) {
    return [];
  }

  if (typeof fallbackLng === 'string') {
    return [fallbackLng];
  }

  if (Array.isArray(fallbackLng)) {
    return fallbackLng.filter((value): value is string => typeof value === 'string');
  }

  if (typeof fallbackLng === 'object') {
    return Object.values(fallbackLng).flatMap((value) => getFallbackLanguages(value));
  }

  return [];
}

function resolveNestedValue(resourceTree: ResourceTree, keyPrefix: string) {
  return keyPrefix
    .split('.')
    .reduce<unknown>(
      (currentValue, key) =>
        currentValue && typeof currentValue === 'object'
          ? (currentValue as Record<string, unknown>)[key]
          : undefined,
      resourceTree,
    );
}

function normalizeListValue(value: unknown) {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }

  if (value && typeof value === 'object') {
    return Object.entries(value)
      .sort(([left], [right]) => Number(left) - Number(right))
      .map(([, item]) => item)
      .filter((item): item is string => typeof item === 'string');
  }

  return [];
}

export function getTranslationList(
  i18n: {
    language?: string;
    options?: { fallbackLng?: unknown };
    getResourceBundle: (language: string, namespace: string) => ResourceTree | undefined;
  },
  namespace: string,
  keyPrefix: string,
) {
  const fallbackLanguages = getFallbackLanguages(i18n.options?.fallbackLng);
  const languages = [
    i18n.language,
    i18n.language?.split('-')[0],
    ...fallbackLanguages,
    ...fallbackLanguages.map((value) => value.split('-')[0]),
    'en',
  ].filter(
    (value, index, allValues): value is string => !!value && allValues.indexOf(value) === index,
  );

  const flatPrefix = `${keyPrefix}.`;

  for (const language of languages) {
    const resourceBundle = i18n.getResourceBundle(language, namespace);

    if (!resourceBundle) {
      continue;
    }

    const nestedValue = resolveNestedValue(resourceBundle, keyPrefix);
    const nestedList = normalizeListValue(nestedValue);

    if (nestedList.length > 0) {
      return nestedList;
    }

    const flatList = Object.entries(resourceBundle)
      .filter(([key, value]) => key.startsWith(flatPrefix) && typeof value === 'string')
      .sort(
        ([left], [right]) =>
          Number(left.replace(flatPrefix, '')) - Number(right.replace(flatPrefix, '')),
      )
      .map(([, value]) => value as string);

    if (flatList.length > 0) {
      return flatList;
    }
  }

  return [];
}

export function translateArray<T>(
  t: TranslateFunction,
  key: string,
  options: TranslationOptions = {},
): T[] {
  const value = t(key, { ...options, returnObjects: true });

  if (Array.isArray(value)) {
    return value as T[];
  }

  const error = new Error(`Expected translation array for "${key}", got ${describeValue(value)}`);

  if (process.env.NODE_ENV !== 'production') {
    throw error;
  }

  if (isMissingTranslationValue(value, key)) {
    return [];
  }

  monitoring.Sentry.captureException(error);

  return [];
}
